import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { Direction } from '@shared/types/entity';
import { getBodyAnimKey } from '../../sprites/BodySpriteManager';
import { getWeaponAnimKey } from '../../sprites/WeaponSpriteManager';
import { getShieldAnimKey } from '../../sprites/ShieldSpriteManager';

/**
 * Visor de gráficos indexeados in-game (dev/admin). Lista las texturas de personaje/ítem que están
 * REALMENTE cargadas en el `TextureManager` de Phaser, filtradas por prefijo de clave (body/head/
 * weapon/shield/helm/boot/item_icon_), en una grilla de miniaturas navegable por categoría. Al
 * clickear una miniatura muestra un detalle con el sprite grande, sus dimensiones, la cantidad de
 * frames y botones para recorrerlos (+ reproducir la animación de caminar si existe).
 *
 * Es 100% client-side (sólo lee lo indexado, no toca el server). Panel Phaser fijo (scrollFactor 0)
 * con scroll por rueda sobre un container enmascarado, mismo andamiaje que `SoundBrowserPanel`. Se
 * togglea desde un botón de la fila dev/debug del menú. Ver docs/TEXTURE_BROWSER.md y SPRITE_SYSTEM.md.
 */

/** Categoría del visor: cómo se detecta y ordena cada familia de texturas. */
interface TextureCategory {
  key: string;
  label: string;
  /** Prefijo de la texture key (los numéricos exigen que siga un dígito). */
  prefix: string;
  /** true = key es `<prefix><id>` numérico; false = `<prefix><nombre>` (íconos). */
  numeric: boolean;
}

// Orden de las categorías (también define el orden en "Todos"). Los prefijos salen de los
// sprite managers: getBodySpriteKey→`body`, getHeadSpriteKey→`head`, etc.; los íconos de ítem
// son `item_icon_<icon>` (convención usada en GameScene/ExchangePanel/CraftingPanel).
const CATEGORIES: TextureCategory[] = [
  { key: 'body', label: 'Cuerpos', prefix: 'body', numeric: true },
  { key: 'head', label: 'Cabezas', prefix: 'head', numeric: true },
  { key: 'weapon', label: 'Armas', prefix: 'weapon', numeric: true },
  { key: 'shield', label: 'Escudos', prefix: 'shield', numeric: true },
  { key: 'helm', label: 'Cascos', prefix: 'helm', numeric: true },
  { key: 'boot', label: 'Botas', prefix: 'boot', numeric: true },
  { key: 'item_icon_', label: 'Íconos', prefix: 'item_icon_', numeric: false },
];

export class TextureBrowserPanel {
  private container: Phaser.GameObjects.Container | null = null;
  private inner: Phaser.GameObjects.Container | null = null; // grilla scrolleable (enmascarada)
  private mask: Phaser.Display.Masks.GeometryMask | null = null;
  private chips: Phaser.GameObjects.Text[] = [];
  private detail: Phaser.GameObjects.Container | null = null;

  private visible = false;
  private view: 'grid' | 'detail' = 'grid';
  private currentCategory = 'body';
  private scrollY = 0;
  private contentHeight = 0;
  private viewHeight = 0;

  // Geometría del panel (para hit-test de la rueda).
  private px = 0;
  private py = 0;
  private pw = 0;
  private ph = 0;
  private listTop = 0;

  // Estado del detalle. Sprite (no Image) para poder reproducir animaciones además de frames sueltos.
  private detailSprite: Phaser.GameObjects.Sprite | null = null;
  private detailInfo: Phaser.GameObjects.Text | null = null;
  private detailFrames: string[] = [];
  private detailFrameIdx = 0;
  private detailKey = '';
  private detailAnimKey: string | null = null;
  private detailPlaying = false;
  private detailPlayBtn: Phaser.GameObjects.Text | null = null;

  private static readonly WIDTH = 560;
  private static readonly DEPTH = 1200;
  private static readonly CELL = 84; // ancho/alto de celda (miniatura 48 + label)
  private static readonly THUMB = 48; // lado del recuadro de la miniatura
  private static readonly PAD = 12;

  constructor(private scene: GameScene) {}

  public isVisible(): boolean {
    return this.visible;
  }

  public toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  public show(): void {
    if (!this.container) this.build();
    this.visible = true;
    this.container?.setVisible(true);
    // Re-enumerar por si cambió lo indexado (y refrescar conteos de chips).
    this.selectCategory(this.currentCategory);
  }

  public hide(): void {
    this.visible = false;
    this.stopDetailAnim();
    this.container?.setVisible(false);
  }

  private build(): void {
    const { width, height } = this.scene.scale;
    const panelW = TextureBrowserPanel.WIDTH;
    const panelH = Math.min(560, height - 80);
    const x = Math.max(20, width - panelW - 20);
    const y = 60;
    this.px = x;
    this.py = y;
    this.pw = panelW;
    this.ph = panelH;

    this.container = this.scene.add
      .container(x, y)
      .setDepth(TextureBrowserPanel.DEPTH)
      .setScrollFactor(0);

    const bg = this.scene.add
      .rectangle(0, 0, panelW, panelH, 0x1a1a1a, 0.96)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x555555);
    const title = this.scene.addText(10, 8, 'Visor de gráficos', {
      fontSize: '15px',
      color: '#ffffff',
    });
    const close = this.scene
      .addText(panelW - 22, 6, '✕', {
        fontSize: '15px',
        color: '#ffffff',
        backgroundColor: '#553333',
        padding: { left: 6, right: 6, top: 2, bottom: 2 },
      })
      .setInteractive({ useHandCursor: true });
    close.on('pointerup', () => this.hide());

    this.container.add([bg, title, close]);

    // Fila de chips de categoría (Todos + una por familia). Pueden ocupar hasta 2 filas.
    this.buildChips();

    // Área scrolleable de la grilla (debajo del bloque de chips, ~2 filas).
    this.listTop = 82;
    this.viewHeight = panelH - this.listTop - 8;
    this.inner = this.scene.add.container(0, this.listTop).setScrollFactor(0);
    this.container.add(this.inner);

    const maskShape = this.scene.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(x, y + this.listTop, panelW, this.viewHeight);
    this.mask = maskShape.createGeometryMask();
    this.inner.setMask(this.mask);

    this.buildDetail(panelW, panelH);
    this.selectCategory(this.currentCategory);

    // Scroll por rueda cuando el puntero está sobre el panel (sólo en vista grilla).
    this.scene.input.on(
      'wheel',
      (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
        if (!this.visible || this.view !== 'grid') return;
        const ptr = this.scene.input.activePointer;
        if (ptr.x < this.px || ptr.x > this.px + this.pw) return;
        if (ptr.y < this.py || ptr.y > this.py + this.ph) return;
        this.scrollBy(dy);
      }
    );
  }

  private buildChips(): void {
    if (!this.container) return;
    // "Todos" + una por categoría. Posiciones y conteos se fijan en refreshChipLabels()/reflowChips()
    // (el ancho depende del conteo, que se conoce recién al abrir).
    const chipDefs = [{ key: 'all', label: 'Todos' }, ...CATEGORIES];
    for (const def of chipDefs) {
      const chip = this.scene
        .addText(10, 34, def.label, {
          fontSize: '11px',
          color: '#dddddd',
          backgroundColor: '#3a3a3a',
          padding: { left: 6, right: 6, top: 3, bottom: 3 },
        })
        .setInteractive({ useHandCursor: true });
      chip.setData('catKey', def.key);
      chip.on('pointerup', () => this.selectCategory(def.key));
      this.chips.push(chip);
      this.container.add(chip);
    }
  }

  private refreshChipLabels(): void {
    for (const chip of this.chips) {
      const catKey = chip.getData('catKey') as string;
      const count = this.getKeysForCategory(catKey).length;
      const label =
        catKey === 'all' ? 'Todos' : CATEGORIES.find((c) => c.key === catKey)?.label ?? catKey;
      chip.setText(`${label} (${count})`);
      const active = catKey === this.currentCategory;
      chip.setBackgroundColor(active ? '#6a5a2a' : '#3a3a3a');
      chip.setColor(active ? '#ffe9a8' : '#dddddd');
    }
    this.reflowChips();
  }

  /** Reposiciona los chips en filas con wrap (sus anchos cambian con el conteo). */
  private reflowChips(): void {
    const rowH = 22;
    const startX = 10;
    const startY = 32;
    let cx = startX;
    let cy = startY;
    for (const chip of this.chips) {
      if (cx + chip.width > this.pw - 10 && cx > startX) {
        cx = startX;
        cy += rowH;
      }
      chip.setPosition(cx, cy);
      cx += chip.width + 6;
    }
  }

  private selectCategory(catKey: string): void {
    this.currentCategory = catKey;
    this.scrollY = 0;
    this.refreshChipLabels();
    this.showGridView();
    this.populateGrid();
  }

  /** ¿La key pertenece a esta categoría y su textura está sana (no __MISSING)? */
  private matchesCategory(key: string, cat: TextureCategory): boolean {
    if (!key.startsWith(cat.prefix)) return false;
    if (cat.numeric) {
      const rest = key.slice(cat.prefix.length);
      if (!/^\d+$/.test(rest)) return false; // exige `<prefix><número>` exacto (body12, no bodyfoo)
    }
    return true;
  }

  /** Keys de una categoría (o de todas), ordenadas y filtradas a texturas realmente cargadas. */
  private getKeysForCategory(catKey: string): string[] {
    const cats = catKey === 'all' ? CATEGORIES : CATEGORIES.filter((c) => c.key === catKey);
    const allKeys = this.scene.textures.getTextureKeys();
    const out: string[] = [];
    for (const cat of cats) {
      const matched = allKeys.filter((k) => {
        if (k.startsWith('__')) return false;
        if (this.scene.textures.get(k).key === '__MISSING') return false;
        return this.matchesCategory(k, cat);
      });
      if (cat.numeric) {
        matched.sort(
          (a, b) => parseInt(a.slice(cat.prefix.length), 10) - parseInt(b.slice(cat.prefix.length), 10)
        );
      } else {
        matched.sort();
      }
      out.push(...matched);
    }
    return out;
  }

  /** Nombre del primer frame de una textura (para la miniatura); null = usar frame base. */
  private firstFrameName(key: string): string | null {
    const names = this.scene.textures.get(key).getFrameNames();
    return names.length > 0 ? names[0] : null;
  }

  private populateGrid(): void {
    if (!this.inner) return;
    // Destruir miniaturas anteriores (rebuild acota el nº de objetos vivos por categoría).
    this.inner.removeAll(true);
    this.scrollY = 0;
    this.inner.setY(this.listTop);

    const keys = this.getKeysForCategory(this.currentCategory);
    const { CELL, THUMB, PAD } = TextureBrowserPanel;
    const cols = Math.max(1, Math.floor((this.pw - PAD) / CELL));

    keys.forEach((key, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = PAD + col * CELL + CELL / 2;
      const cy = row * CELL + CELL / 2;

      const box = this.scene.add
        .rectangle(cx, cy - 6, THUMB + 6, THUMB + 6, 0x000000, 0.6)
        .setStrokeStyle(1, 0x444444)
        .setInteractive({ useHandCursor: true });
      box.on('pointerover', () => box.setStrokeStyle(1, 0xffe9a8));
      box.on('pointerout', () => box.setStrokeStyle(1, 0x444444));
      box.on('pointerup', () => this.openDetail(key));

      const frame = this.firstFrameName(key);
      const img =
        frame !== null
          ? this.scene.add.image(cx, cy - 6, key, frame)
          : this.scene.add.image(cx, cy - 6, key);
      this.fitImage(img, THUMB);

      // Label: el id (numéricos) o el nombre de ícono, recortado.
      const shortLabel = this.labelForKey(key);
      const label = this.scene.addText(cx, cy + THUMB / 2 - 2, shortLabel, {
        fontSize: '9px',
        color: '#bbbbbb',
      }).setOrigin(0.5, 0);

      this.inner!.add([box, img, label]);
    });

    const rows = Math.ceil(keys.length / cols);
    this.contentHeight = rows * CELL + 8;
  }

  /** Etiqueta corta bajo la miniatura: id para numéricos, nombre para íconos. */
  private labelForKey(key: string): string {
    for (const cat of CATEGORIES) {
      if (this.matchesCategory(key, cat)) {
        const rest = key.slice(cat.prefix.length);
        return rest.length > 12 ? rest.slice(0, 11) + '…' : rest;
      }
    }
    return key;
  }

  /** Escala una imagen para caber dentro de un cuadro `box` conservando el aspecto (sin achicar de más). */
  private fitImage(img: Phaser.GameObjects.Image, box: number): void {
    const w = img.width || 1;
    const h = img.height || 1;
    const scale = Math.min(box / w, box / h);
    img.setScale(scale);
  }

  private scrollBy(dy: number): void {
    if (!this.inner) return;
    const maxScroll = Math.max(0, this.contentHeight - this.viewHeight);
    this.scrollY = Phaser.Math.Clamp(this.scrollY + dy * 0.5, 0, maxScroll);
    this.inner.setY(this.listTop - this.scrollY);
  }

  // ------------------------------------------------------------------
  // Vista de detalle
  // ------------------------------------------------------------------

  private buildDetail(panelW: number, panelH: number): void {
    if (!this.container) return;
    this.detail = this.scene.add.container(0, 0).setScrollFactor(0).setVisible(false);
    this.container.add(this.detail);

    const back = this.scene
      .addText(10, 34, '‹ Volver', {
        fontSize: '12px',
        color: '#ffffff',
        backgroundColor: '#3a3a3a',
        padding: { left: 6, right: 6, top: 3, bottom: 3 },
      })
      .setInteractive({ useHandCursor: true });
    back.on('pointerup', () => this.showGridView());

    // Recuadro grande del sprite.
    const boxSize = 200;
    const boxX = panelW / 2;
    const boxY = 90 + boxSize / 2;
    const box = this.scene.add
      .rectangle(boxX, boxY, boxSize, boxSize, 0x000000, 0.7)
      .setStrokeStyle(1, 0x555555);
    this.detailSprite = this.scene.add.sprite(boxX, boxY, '__DEFAULT');

    this.detailInfo = this.scene
      .addText(panelW / 2, boxY + boxSize / 2 + 12, '', {
        fontSize: '12px',
        color: '#dddddd',
        align: 'center',
        wordWrap: { width: panelW - 40 },
      })
      .setOrigin(0.5, 0);

    const btnY = panelH - 40;
    const prev = this.mkButton(panelW / 2 - 90, btnY, '◀ Frame', () => this.stepFrame(-1));
    const next = this.mkButton(panelW / 2 + 40, btnY, 'Frame ▶', () => this.stepFrame(1));
    const play = this.mkButton(panelW / 2 - 24, btnY - 30, '▶ Animar', () => this.toggleDetailAnim());
    this.detailPlayBtn = play;

    this.detail.add([back, box, this.detailSprite, this.detailInfo, prev, next, play]);
  }

  private openDetail(key: string): void {
    this.detailKey = key;
    this.detailFrames = this.scene.textures.get(key).getFrameNames();
    this.detailFrameIdx = 0;
    this.detailAnimKey = this.animKeyForKey(key);
    this.stopDetailAnim();
    this.applyDetailFrame();
    this.updatePlayButton();
    this.view = 'detail';
    this.setGridVisible(false);
    this.detail?.setVisible(true);
  }

  /** Muestra el frame actual (o el base) en el sprite de detalle y refresca la info. */
  private applyDetailFrame(): void {
    if (!this.detailSprite) return;
    const frame = this.detailFrames[this.detailFrameIdx];
    if (frame !== undefined) {
      this.detailSprite.setTexture(this.detailKey, frame);
    } else {
      this.detailSprite.setTexture(this.detailKey);
    }
    this.detailSprite.setScale(1);
    this.fitImage(this.detailSprite, 190);
    this.refreshDetailInfo();
  }

  private refreshDetailInfo(): void {
    if (!this.detailInfo || !this.detailSprite) return;
    const total = this.detailFrames.length || 1;
    const fw = Math.round(this.detailSprite.frame.width);
    const fh = Math.round(this.detailSprite.frame.height);
    const lines = [
      this.detailKey,
      `${fw}×${fh} px · frame ${this.detailFrameIdx + 1}/${total}`,
    ];
    this.detailInfo.setText(lines.join('\n'));
  }

  private stepFrame(delta: number): void {
    if (this.detailFrames.length === 0) return;
    this.stopDetailAnim();
    this.detailFrameIdx =
      (this.detailFrameIdx + delta + this.detailFrames.length) % this.detailFrames.length;
    this.applyDetailFrame();
  }

  /** Construye el animKey de caminar (dir ABAJO) para body/weapon/shield; null para el resto. */
  private animKeyForKey(key: string): string | null {
    const num = (prefix: string): number => parseInt(key.slice(prefix.length), 10);
    if (this.matchesCategory(key, CATEGORIES[0])) return getBodyAnimKey(num('body'), Direction.DOWN);
    if (this.matchesCategory(key, CATEGORIES[2]))
      return getWeaponAnimKey(num('weapon'), Direction.DOWN);
    if (this.matchesCategory(key, CATEGORIES[3]))
      return getShieldAnimKey(num('shield'), Direction.DOWN);
    return null;
  }

  private toggleDetailAnim(): void {
    if (this.detailPlaying) {
      this.stopDetailAnim();
      return;
    }
    if (!this.detailSprite || !this.detailAnimKey) return;
    if (!this.scene.anims.exists(this.detailAnimKey)) return;
    this.detailSprite.play(this.detailAnimKey, true);
    this.detailPlaying = true;
    this.updatePlayButton();
  }

  private stopDetailAnim(): void {
    if (!this.detailSprite) return;
    if (this.detailPlaying) {
      this.detailSprite.stop();
      this.detailPlaying = false;
      // Volver al frame estático seleccionado (setTexture directo para no re-entrar en stop).
      const frame = this.detailFrames[this.detailFrameIdx];
      if (frame !== undefined) this.detailSprite.setTexture(this.detailKey, frame);
      else this.detailSprite.setTexture(this.detailKey);
      this.detailSprite.setScale(1);
      this.fitImage(this.detailSprite, 190);
      this.refreshDetailInfo();
    }
    this.updatePlayButton();
  }

  private updatePlayButton(): void {
    const play = this.detailPlayBtn;
    if (!play) return;
    const hasAnim = !!this.detailAnimKey && this.scene.anims.exists(this.detailAnimKey);
    play.setVisible(hasAnim);
    play.setText(this.detailPlaying ? '⏸ Parar' : '▶ Animar');
  }

  // ------------------------------------------------------------------
  // Cambios de vista
  // ------------------------------------------------------------------

  private showGridView(): void {
    this.view = 'grid';
    this.stopDetailAnim();
    this.detail?.setVisible(false);
    this.setGridVisible(true);
  }

  private setGridVisible(visible: boolean): void {
    this.inner?.setVisible(visible);
    for (const chip of this.chips) chip.setVisible(visible);
  }

  private mkButton(
    x: number,
    y: number,
    label: string,
    onClick: () => void
  ): Phaser.GameObjects.Text {
    const t = this.scene
      .addText(x, y, label, {
        fontSize: '11px',
        color: '#ffffff',
        backgroundColor: '#3a3a3a',
        padding: { left: 6, right: 6, top: 3, bottom: 3 },
      })
      .setInteractive({ useHandCursor: true });
    t.on('pointerover', () => t.setBackgroundColor('#555555'));
    t.on('pointerout', () => t.setBackgroundColor('#3a3a3a'));
    t.on('pointerup', () => onClick());
    return t;
  }
}
